import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Eye, Check, X, MessageSquare, Clock, Filter } from 'lucide-react';
import api from '../../api/api';

const ContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [updateLoading, setUpdateLoading] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, [filterStatus]);

  const fetchMessages = async () => {
    console.log('=== FETCH MESSAGES START ===');
    setLoading(true);
    setError(null);
    
    // Check if admin token exists
    const adminToken = localStorage.getItem('adminToken');
    console.log('Admin token exists:', !!adminToken);
    console.log('Admin token (first 20 chars):', adminToken?.substring(0, 20));
    
    if (!adminToken) {
      console.error('❌ No admin token found');
      setError('Not authenticated as admin');
      setLoading(false);
      return;
    }
    
    try {
      console.log('📡 Making API call to /admin/contact with filter:', filterStatus);
      console.log('Full URL will be:', api.defaults.baseURL + '/admin/contact?status=' + filterStatus);
      
      const response = await api.get(`/admin/contact?status=${filterStatus}`);
      
      console.log('✅ API Response received');
      console.log('Response status:', response.status);
      console.log('Response data:', JSON.stringify(response.data, null, 2));
      console.log('Success flag:', response.data.success);
      console.log('Messages count:', response.data.messages?.length);
      console.log('Messages array:', response.data.messages);
      console.log('Unread count:', response.data.unreadCount);
      
      if (response.data.success) {
        // Backend now returns data directly (not nested)
        const msgs = response.data.messages || [];
        const unread = response.data.unreadCount || 0;
        
        console.log('✅ Messages extracted:', msgs.length, 'messages');
        console.log('✅ Unread count:', unread);
        console.log('✅ First message:', msgs[0]);
        
        setMessages(msgs);
        setUnreadCount(unread);
        
        console.log('✅ State updated successfully');
      } else {
        console.error('❌ API returned success=false:', response.data);
        setError(response.data.error || 'Failed to load messages');
      }
    } catch (err) {
      console.error('❌ Error fetching contact messages:', err);
      console.error('Error message:', err.message);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      console.error('Error config:', err.config);
      setError(err.response?.data?.error || 'Failed to load contact messages');
    } finally {
      setLoading(false);
      console.log('=== FETCH MESSAGES END ===');
    }
  };

  const updateMessageStatus = async (messageId, status) => {
    setUpdateLoading(true);
    try {
      const response = await api.put(`/admin/contact/${messageId}`, { status });
      if (response.data.success) {
        fetchMessages();
        if (selectedMessage && selectedMessage.id === messageId) {
          setSelectedMessage(response.data.data);
        }
      }
    } catch (err) {
      console.error('Error updating message:', err);
      alert('Failed to update message status');
    } finally {
      setUpdateLoading(false);
    }
  };

  const addAdminNotes = async (messageId, adminNotes) => {
    setUpdateLoading(true);
    try {
      const response = await api.put(`/admin/contact/${messageId}`, { adminNotes });
      if (response.data.success) {
        fetchMessages();
        if (selectedMessage && selectedMessage.id === messageId) {
          setSelectedMessage(response.data.data);
        }
      }
    } catch (err) {
      console.error('Error updating notes:', err);
      alert('Failed to update notes');
    } finally {
      setUpdateLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      unread: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Unread' },
      read: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Read' },
      replied: { bg: 'bg-green-100', text: 'text-green-800', label: 'Replied' }
    };
    const config = statusConfig[status] || statusConfig.unread;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mail size={28} />
            Contact Messages
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {unreadCount > 0 && (
              <span className="font-semibold text-yellow-600">
                {unreadCount} unread message{unreadCount !== 1 ? 's' : ''}
              </span>
            )}
            {unreadCount === 0 && "All messages have been read"}
          </p>
        </div>
        
        {/* Filter Buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterStatus === 'all'
                ? 'bg-black text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilterStatus('unread')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterStatus === 'unread'
                ? 'bg-yellow-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Unread
            {unreadCount > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-white text-yellow-600 rounded-full text-xs font-bold">
                {unreadCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setFilterStatus('replied')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterStatus === 'replied'
                ? 'bg-green-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Replied
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading messages...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Messages List */}
      {!loading && !error && (
        <div className="grid grid-cols-1 gap-4">
          {messages.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <MessageSquare size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">
                {filterStatus === 'all' ? 'No contact messages yet' : `No ${filterStatus} messages`}
              </p>
            </div>
          ) : (
            messages.map((message) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white border-2 rounded-lg p-6 hover:shadow-md transition-all cursor-pointer ${
                  message.status === 'unread' ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200'
                }`}
                onClick={() => setSelectedMessage(message)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-gray-900">{message.name}</h3>
                      {getStatusBadge(message.status)}
                    </div>
                    <p className="text-sm text-gray-600">{message.email}</p>
                  </div>
                  <div className="text-xs text-gray-500 flex items-center gap-1">
                    <Clock size={14} />
                    {formatDate(message.createdAt)}
                  </div>
                </div>

                <p className="text-gray-700 line-clamp-2">{message.message}</p>

                <div className="mt-4 flex gap-2">
                  {message.status === 'unread' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        updateMessageStatus(message.id, 'read');
                      }}
                      disabled={updateLoading}
                      className="px-3 py-1 bg-blue-500 text-white rounded text-xs font-medium hover:bg-blue-600 transition-colors disabled:opacity-50"
                    >
                      Mark as Read
                    </button>
                  )}
                  {message.status !== 'replied' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        updateMessageStatus(message.id, 'replied');
                      }}
                      disabled={updateLoading}
                      className="px-3 py-1 bg-green-500 text-white rounded text-xs font-medium hover:bg-green-600 transition-colors disabled:opacity-50"
                    >
                      Mark as Replied
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedMessage(message);
                    }}
                    className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs font-medium hover:bg-gray-300 transition-colors"
                  >
                    View Details
                  </button>
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* Message Detail Modal */}
      {selectedMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-xl font-bold text-gray-900">Message Details</h3>
              <button
                onClick={() => setSelectedMessage(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Sender Info */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">From</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-semibold text-gray-900">{selectedMessage.name}</p>
                  <p className="text-sm text-gray-600">{selectedMessage.email}</p>
                  <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                    <Clock size={12} />
                    Received: {formatDate(selectedMessage.createdAt)}
                  </p>
                </div>
              </div>

              {/* Message */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Message</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-800 whitespace-pre-wrap">{selectedMessage.message}</p>
                </div>
              </div>

              {/* Status */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Status</h4>
                <div className="flex gap-2">
                  {getStatusBadge(selectedMessage.status)}
                  {selectedMessage.readAt && (
                    <span className="text-xs text-gray-500">
                      Read: {formatDate(selectedMessage.readAt)}
                    </span>
                  )}
                  {selectedMessage.repliedAt && (
                    <span className="text-xs text-gray-500">
                      Replied: {formatDate(selectedMessage.repliedAt)}
                    </span>
                  )}
                </div>
              </div>

              {/* Admin Notes */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Admin Notes</h4>
                <textarea
                  defaultValue={selectedMessage.adminNotes || ''}
                  onBlur={(e) => {
                    if (e.target.value !== selectedMessage.adminNotes) {
                      addAdminNotes(selectedMessage.id, e.target.value);
                    }
                  }}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  rows="3"
                  placeholder="Add internal notes about this message..."
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                {selectedMessage.status === 'unread' && (
                  <button
                    onClick={() => updateMessageStatus(selectedMessage.id, 'read')}
                    disabled={updateLoading}
                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 font-medium"
                  >
                    Mark as Read
                  </button>
                )}
                {selectedMessage.status !== 'replied' && (
                  <button
                    onClick={() => updateMessageStatus(selectedMessage.id, 'replied')}
                    disabled={updateLoading}
                    className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 font-medium"
                  >
                    Mark as Replied
                  </button>
                )}
                <a
                  href={`mailto:${selectedMessage.email}`}
                  className="flex-1 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-center font-medium"
                >
                  Reply via Email
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ContactMessages;
